/**
 * EL HISTORIAL DE TORNEOS: los que ya terminaron, de todos mis clubes.
 *
 * UN TORNEO TERMINADO NO SE ESCONDE. Es el registro de que eso pasó, y la
 * ficha siempre siguió abierta — pero para llegar había que acordarse de en
 * qué club fue, entrar al club, y bajar por su lista hasta el grupo de
 * terminados. Acá están todos juntos, del más nuevo al más viejo.
 *
 * CADA FILA DICE MES, AÑO Y CLUB. El día exacto no es lo que se recuerda de
 * un torneo de hace dos años; el mes sí, y el club es lo que separa dos
 * torneos que se llaman parecido.
 *
 * LOS FILTROS TRABAJAN SOBRE LO QUE YA ESTÁ, sin volver a preguntarle al
 * servidor. Son los torneos de los clubes de una persona —decenas, no
 * millones— y pedirle al servidor cada vez que alguien toca un desplegable
 * es un viaje por una cuenta que se hace sola.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { TournamentHistoryRow } from '@tennisup/contracts';

import { Alert } from '../components/Alert';
import { AppLayout } from '../components/AppLayout';
import { Paginador, usePaginado } from '../components/Paginado';
import { BackButton } from '../components/BackButton';
import { Select } from '../components/Select';
import { api } from '../lib/api';
import { errorMessage } from '../lib/errorMessage';
import { monthYear } from '../lib/monthYear';

/** El valor de «sin filtrar». Vacío no sirve: un club podría llamarse así. */
const TODOS = '__todos__';

export function TournamentHistoryPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const [filas, setFilas] = useState<TournamentHistoryRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  /**
   * UN RANGO DE FECHAS Y NO UN AÑO.
   *
   * Acá había un desplegable de años. Javier pidió el rango, y es mejor por
   * dos cosas: un año no sirve para «la temporada pasada» —que arranca en
   * agosto y termina en mayo— y el desplegable solo ofrecía los años que YA
   * tenían torneos, así que no había forma de preguntar por un pedazo de uno.
   *
   * `''` es «sin poner». Los dos extremos son opcionales y por separado:
   * «desde marzo» y «hasta marzo» son dos preguntas distintas y las dos se
   * hacen.
   */
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [club, setClub] = useState(TODOS);

  const load = useCallback(async () => {
    try {
      setFilas(await api.myFinishedTournaments());
    } catch (caught) {
      setError(errorMessage(t, caught));
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const todas = useMemo(() => filas ?? [], [filas]);

  /**
   * LOS CLUBES SALEN DE LO QUE HAY, no de una lista fija.
   *
   * Ofrecer un club sin un solo torneo terminado es ofrecer un filtro que
   * solo puede vaciar la lista, y quien lo toca cree que se rompió algo.
   */
  const clubes = useMemo(() => {
    const porId = new Map(todas.map((f) => [f.communityId, f.communityName]));
    return [...porId].sort((a, b) => a[1].localeCompare(b[1], 'es'));
  }, [todas]);

  /*
   * LAS FECHAS SE COMPARAN COMO TEXTO, y no es un atajo sucio: `startsAt` es
   * una fecha civil `YYYY-MM-DD` y los dos campos del formulario también, así
   * que el orden alfabético ES el orden cronológico. Convertirlas a `Date`
   * para compararlas metería la zona horaria del navegador en una pregunta
   * que no la tiene, y correría un torneo un día para media Costa Rica.
   *
   * LOS DOS EXTREMOS INCLUSIVE: quien pone «hasta el 30» espera ver lo del 30.
   */
  const visibles = todas.filter(
    (fila) =>
      (desde === '' || fila.startsAt >= desde) &&
      (hasta === '' || fila.startsAt <= hasta) &&
      (club === TODOS || fila.communityId === club),
  );

  /**
   * DE A DIEZ. `reiniciarCon` son los dos filtros: cambiar de año o de club
   * trae OTRA lista, y quedarse en la página 3 de la anterior se lee como que
   * el filtro no encontró nada.
   */
  const pagina = usePaginado(visibles, `${desde}|${hasta}|${club}`);

  return (
    <AppLayout>
      <BackButton to="/torneos" />

      <h1>{t('history.title')}</h1>
      <p className="lead">{t('history.lead')}</p>

      {error !== null && <Alert kind="error">{error}</Alert>}

      {/* LOS FILTROS SOLO APARECEN CUANDO HAY MÁS DE UNA COSA QUE ELEGIR.
          Un desplegable con una sola opción no filtra nada: es un control que
          se ve y no hace, y hace dudar de si está roto. */}
      {todas.length > 1 && (
        <div className="history-range">
          <div className="field">
            <label htmlFor="history-from">{t('history.from')}</label>
            <input
              id="history-from"
              type="date"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="history-to">{t('history.to')}</label>
            <input
              id="history-to"
              type="date"
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
            />
          </div>
        </div>
      )}

      {clubes.length > 1 && (
        <div className="field">
          <label htmlFor="history-club">{t('history.club')}</label>
          <Select
            id="history-club"
            value={club}
            options={[
              { value: TODOS, label: t('history.allClubs') },
              ...clubes.map(([id, nombre]) => ({ value: id, label: nombre })),
            ]}
            onChange={setClub}
          />
        </div>
      )}

      {filas === null ? (
        error === null ? <p className="help">{t('common.loading')}</p> : null
      ) : visibles.length === 0 ? (
        // Se distingue «no hay nada todavía» de «tu filtro no encontró nada»:
        // son dos situaciones distintas y la segunda tiene arreglo.
        <p className="help">{todas.length === 0 ? t('history.empty') : t('history.noneFound')}</p>
      ) : (
        <>
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">{t('history.tournament')}</th>
                <th scope="col">{t('history.when')}</th>
                <th scope="col" className="num">
                  {t('history.players')}
                </th>
              </tr>
            </thead>
            <tbody>
              {pagina.visibles.map((fila) => (
                <tr key={fila.id}>
                  <td>
                    <Link to={`/torneos/${fila.id}`}>{fila.name}</Link>
                    {/* EL CLUB DEBAJO DEL NOMBRE Y NO EN SU PROPIA COLUMNA:
                        una cuarta columna en un teléfono deja las cuatro
                        ilegibles, y el club se lee junto al nombre, que es
                        con lo que se distingue un torneo de otro parecido. */}
                    <span className="history-club">{fila.communityName}</span>
                  </td>
                  <td>{monthYear(fila.startsAt, i18n.language)}</td>
                  <td className="num">{fila.entryCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Paginador pagina={pagina.pagina} paginas={pagina.paginas} irA={pagina.irA} />
        </>
      )}
    </AppLayout>
  );
}
